// Laying the persona menu's positions out as a tree.
//
// A user's own positions rarely connect to each other: four sibling domains routinely share a
// parent the user does not hold, so a tree built from held positions alone is a flat list wearing
// tree glyphs. The ancestors the login returns separately are what join them up -- they are shown
// as context and cannot be chosen, because they are not personas this user has.

export type TreeNode = {
    USID: string;
    Name: string;
    ParentUSID?: string | null;
};

export type TreeRow = {
    node: TreeNode;
    // How deep. 0 is a root and draws no guides -- a lone elbow against nothing reads as a broken
    // tree rather than a root.
    depth: number;
    // One entry per ANCESTOR column, root-most first: true where that ancestor still has siblings
    // below it, so its vertical line has to continue past this row. This is the shape t6's
    // CustomGridTreeDataGroupingCell uses, and it is deliberately not a string of box-drawing
    // characters: those are only font-height inside a taller row, so the verticals break between
    // rows. Drawn as CSS borders on full-height columns they join up.
    guides: boolean[];
    // Last among its siblings, so its own column stops at the elbow instead of carrying on.
    isLast: boolean;
    // Held positions can be switched to. Ancestors are structure, not choices.
    selectable: boolean;
};

export function buildPositionTree(positions: TreeNode[], ancestors: TreeNode[] = []): TreeRow[] {
    const held = new Set(positions.map(p => p.USID));
    const all = [...positions, ...ancestors.filter(a => !held.has(a.USID))];
    const known = new Set(all.map(n => n.USID));

    const byParent = new Map<string, TreeNode[]>();
    const roots: TreeNode[] = [];
    for (const node of all) {
        // A parent nobody sent is not a parent we can draw under, so the node stands as a root.
        // Without this a gap in the ancestor chain would silently drop the whole branch.
        const parent = node.ParentUSID && known.has(node.ParentUSID) ? node.ParentUSID : null;
        if (!parent) {
            roots.push(node);
        } else {
            byParent.set(parent, [...(byParent.get(parent) ?? []), node]);
        }
    }

    const byName = (a: TreeNode, b: TreeNode) => (a.Name ?? "").localeCompare(b.Name ?? "");
    const rows: TreeRow[] = [];

    const walk = (nodes: TreeNode[], guides: boolean[], depth: number) => {
        const sorted = [...nodes].sort(byName);
        sorted.forEach((node, index) => {
            const last = index === sorted.length - 1;
            rows.push({node, depth, guides, isLast: last, selectable: held.has(node.USID)});
            // A child's ancestor columns are this node's, plus one for this node: the line carries
            // on when this node has siblings below it, and stops when it is the last.
            walk(byParent.get(node.USID) ?? [],
                 depth === 0 ? [] : [...guides, !last],
                 depth + 1);
        });
    };

    walk(roots, [], 0);

    // A cycle, or a parent pointing at itself, would leave nodes unvisited. Show them rather than
    // lose them: a persona missing from this menu is a persona the user cannot switch to.
    const shown = new Set(rows.map(r => r.node.USID));
    for (const node of all) {
        if (!shown.has(node.USID)) {
            rows.push({node, depth: 0, guides: [], isLast: true, selectable: held.has(node.USID)});
        }
    }

    return rows;
}
